import { WMSTileLayer } from "react-leaflet";
import { Z } from "../../global/constants/zIndex";
import { geoServerBaseUrl } from "../../services/geoServer";
import { useRasterColormap } from "../../hooks/useRasterColormap";
import { buildRangeSldBody } from "../../global/rangeSld";
import { useActiveRasterTypename } from "../../hooks/useActiveRasterTypename";
import { isProtectedWorkspace } from "../../services/geoServerAuth";
import { AuthenticatedTiles } from "../AuthenticatedWMSTileLayer";

interface RangeStyledWMSLayerProps {
  typename: string;
  workspace: string;
  /** Versão detalhada da camada, usada a partir do limiar de zoom. O recorte
   *  precisa acompanhar essa troca, senão ligar o filtro faria o raster voltar
   *  para a resolução grossa. */
  detailedLayerName?: string;
  detailedLayerZoomThreshold?: number;
  minVal?: number;
  maxVal?: number;
  opacity?: number;
  zIndex?: number;
  /** Dimensão da camada, quando houver: o recorte precisa ser feito sobre a
   *  mesma fatia que estava na tela, não sobre a fatia padrão do GeoServer. */
  elevationValue?: number;
  timeValue?: string;
  /** WMS parameter for `elevationValue`; custom mosaic dimensions use `DIM_<NAME>`. */
  dimensionParam?: string;
}

/**
 * Recorte por faixa de valor renderizado pelo próprio GeoServer, via SLD
 * dinâmico montado a partir do colormap real da camada — ou seja, só os pixels
 * da faixa, com as cores do gradiente original.
 *
 * O endpoint de tiles do backend (ver FilteredRasterLayer) também recorta, mas
 * pinta tudo numa cor sólida; os dois são complementares, não alternativos.
 */
export function RangeStyledWMSLayer({
  typename,
  workspace,
  detailedLayerName,
  detailedLayerZoomThreshold,
  minVal,
  maxVal,
  opacity = 0.8,
  zIndex = Z.MAP_LAYER_DEFAULT,
  elevationValue,
  timeValue,
  dimensionParam = "elevation",
}: RangeStyledWMSLayerProps) {
  const activeTypename = useActiveRasterTypename(
    typename,
    detailedLayerName,
    detailedLayerZoomThreshold
  );
  // O colormap e o SLD saem da camada que está de fato na tela: a versão
  // detalhada pode ter outro estilo, e o SLD nomeia a camada que estiliza.
  const entries = useRasterColormap(workspace, activeTypename);

  // Faixa invertida: a UI permite digitar um mínimo acima do máximo e avisa que
  // nenhum pixel será exibido. Montar o SLD nesse caso geraria ColorMapEntry em
  // ordem decrescente e o GeoServer responderia com ServiceException em vez de
  // um tile transparente — então nem chegamos a pedir o tile.
  if (minVal !== undefined && maxVal !== undefined && minVal > maxVal)
    return null;

  const dimensionParams = {
    ...(elevationValue !== undefined
      ? { [dimensionParam]: elevationValue }
      : {}),
    ...(timeValue !== undefined ? { time: timeValue } : {}),
  };

  // Workspace protegido não pode usar WMSTileLayer: os tiles viriam em `<img>`,
  // que não carrega o cabeçalho de autenticação.
  const protectedWorkspace = isProtectedWorkspace(workspace);

  // Sem o colormap ainda não há cores para preservar no SLD dinâmico. A camada
  // não deve sumir por isso: renderizamos o WMS normal enquanto o colormap
  // carrega ou se o GeoServer não devolver uma rampa válida.
  if (!entries) {
    const unfilteredKey = `${activeTypename}|unfiltered|${elevationValue ?? ""}|${timeValue ?? ""}`;
    if (protectedWorkspace) {
      return (
        <AuthenticatedTiles
          workspace={workspace}
          params={{
            layers: `${workspace}:${activeTypename}`,
            version: "1.1.1",
            ...dimensionParams,
          }}
          opacity={opacity}
          zIndex={zIndex}
          reloadKey={unfilteredKey}
        />
      );
    }
    return (
      <WMSTileLayer
        key={unfilteredKey}
        url={`${geoServerBaseUrl}/${workspace}/wms`}
        layers={`${workspace}:${activeTypename}`}
        format="image/png"
        transparent={true}
        version="1.1.1"
        opacity={opacity}
        zIndex={zIndex}
        {...(dimensionParams as object)}
      />
    );
  }

  const sldBody = buildRangeSldBody({
    workspace,
    layerName: activeTypename,
    entries,
    minVal,
    maxVal,
  });

  const styledKey = `${activeTypename}|${sldBody}|${elevationValue ?? ""}|${timeValue ?? ""}`;

  if (protectedWorkspace) {
    return (
      <AuthenticatedTiles
        workspace={workspace}
        params={{
          layers: `${workspace}:${activeTypename}`,
          version: "1.1.1",
          sld_body: sldBody,
          ...dimensionParams,
        }}
        opacity={opacity}
        zIndex={zIndex}
        reloadKey={styledKey}
      />
    );
  }

  return (
    <WMSTileLayer
      // A troca de faixa muda o SLD, e o WMSTileLayer não redesenha ao mutar
      // params: a key força a substituição da camada Leaflet.
      key={styledKey}
      url={`${geoServerBaseUrl}/${workspace}/wms`}
      layers={`${workspace}:${activeTypename}`}
      format="image/png"
      transparent={true}
      version="1.1.1"
      opacity={opacity}
      zIndex={zIndex}
      {...({
        sld_body: sldBody,
        ...dimensionParams,
      } as object)}
    />
  );
}

export default RangeStyledWMSLayer;
